import execCommand from 'execcommand-copy';
import { useLocalObservable } from 'mobx-react-lite';
import { MouseEvent, useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps } from '@gem/popups';

import Tasks from 'APP/Tasks';
import { ScheduleSettingsPopup } from 'APP/main/PopupManager/Popups/ScheduleSettingsPopup/ScheduleSettingsPopup';
import { ISticker, IStickerPackExtend } from 'APP/model/stickers/stickersModel.types';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { Schedule } from 'STORE/Groups/Schedule';
import { StickerPayload } from 'STORE/Messages/Message/Payload/StickerPayload/StickerPayload';

export interface IStickerPackPopupParams {
  stickerPackId: string;
}

interface IStickerPackPopupPresenter {
  isLoading: boolean;
  isProgress: boolean;
  group: Group | null;
  isAvailableToShow: boolean;
  pack: IStickerPackExtend | null;
  isSharable: boolean;
  isAddedPack: boolean;
  hasBackButton: boolean;
  isAllowedToSendSticker: boolean;

  copyLink(): void;
  toggle(event: MouseEvent): Promise<void>;
  onBack(): void;
  onClose(): void;
  sendSticker(sticker: ISticker): void;
  loadStickerPack(): Promise<void>;
}

export function useStickerPackPopupPresenter({
  popupInstance,
  params,
  onBack,
  onClose,
}: ICommonPopupsProps<IStickerPackPopupParams>): IStickerPackPopupPresenter {
  const { stickerPackId } = params!;
  const { t } = useTranslation();

  const presenter = useLocalObservable<IStickerPackPopupPresenter>(() => ({
    isLoading: false,
    isProgress: false,

    get group(): Group | null {
      return Entities.GroupsStore.activeGroup || null;
    },

    get isAvailableToShow(): boolean {
      return !!presenter.pack || presenter.isLoading;
    },

    get pack(): IStickerPackExtend | null {
      return Entities.stickersStore.getById(stickerPackId);
    },

    get isSharable(): boolean {
      return presenter.pack?.stickerPack.isSharable || false;
    },

    get isAddedPack(): boolean {
      if (!presenter.pack) {
        return false;
      }

      return Entities.stickersStore.installedPackIds.includes(presenter.pack.stickerPack.id);
    },

    get hasBackButton(): boolean {
      return !!onBack;
    },

    get isAllowedToSendSticker(): boolean {
      if (!presenter.group) {
        return false;
      }

      if (presenter.group.isChatGroup) {
        return !presenter.group.withoutMe;
      }
      if (presenter.group.isChannel) {
        return presenter.group.withMeInAdmins;
      }
      return true;
    },

    copyLink(): void {
      if (
        presenter.pack &&
        presenter.pack.stickerPack.isSharable &&
        presenter.pack.stickerPack.deepLink
      ) {
        execCommand.copy(presenter.pack.stickerPack.deepLink.deepLink);
        Entities.toast.show(t('common_link_copied'));
        return;
      }
      Tasks.app.showAlert({
        text: t('common_private_stickers_alert'),
      });
    },

    async toggle(event: MouseEvent): Promise<void> {
      event.stopPropagation();
      event.preventDefault();

      if (!presenter.pack) {
        return;
      }

      presenter.isProgress = true;

      if (presenter.isAddedPack) {
        await Tasks.stickers.removeStickerPackFromProfile(presenter.pack.stickerPack.id);
      } else {
        await Tasks.stickers.addStickerPackToProfile(presenter.pack.stickerPack.id);
      }

      presenter.isProgress = false;
    },

    onClose(): void {
      popupInstance.close();
      onClose?.();
    },

    onBack(): void {
      popupInstance.close();
      onBack?.();
    },

    sendSticker(sticker: ISticker): void {
      if (!presenter.group || !presenter.isAllowedToSendSticker) {
        return;
      }

      const messageData = {
        payload: new StickerPayload(sticker),
        repliedMessage: Entities.InputPanelsStore.activeInputPanel.repliedMessage,
        quote: Entities.InputPanelsStore.activeInputPanel.quote,
      };

      const mustBeScheduled = presenter.group instanceof Schedule;
      const canSend = Tasks.messaging.canSendMessage({
        groupId: presenter.group.id,
        isCheckSlowMode: !mustBeScheduled,
        payload: messageData.payload,
      });

      if (!canSend.result) {
        canSend.error && Tasks.app.showToast(canSend.error);
        return;
      }

      presenter.onClose();

      if (mustBeScheduled) {
        Tasks.app.openPopup(ScheduleSettingsPopup, {
          params: {
            onSubmit: async (date: Date): Promise<void> => {
              await Tasks.messaging.createScheduledMessage((presenter.group as Schedule).groupId!, {
                ...messageData,
                scheduleTime: date.getTime(),
              });
            },
          },
        });

        return;
      }

      Tasks.messaging.createNewMessages(presenter.group.id, [messageData]);
    },

    async loadStickerPack(): Promise<void> {
      if (presenter.pack) {
        return;
      }

      presenter.isLoading = true;
      const isLoaded = await Tasks.stickers.loadStickerPackById(stickerPackId);
      presenter.isLoading = false;

      if (!isLoaded) {
        presenter.onClose();
        Tasks.app.showAlert({
          text: t('stickers_were_deleted_alert'),
        });
      }
    },
  }));

  useEffect(() => {
    presenter.loadStickerPack();
  }, []);

  return presenter;
}
